/**
 * ConfirmContent
 *
 * Content component for confirmation sheet with buttons and loading state.
 * Uses CTAButtons for responsive button layout.
 */

import React, { useState } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { SheetService } from '../../service/SheetService';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { CTAButtons } from '../../service/components/CTAButtons';
import {
  type ExpauseSheetResponse,
  ExpauseSheetResponseType,
} from '../../service/config/ExpauseSheet.types';
import type { ConfirmSheetConfig } from '../ConfirmSheet';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface ConfirmContentProps {
  config: ConfirmSheetConfig;
  onResolve: (response: ExpauseSheetResponse | null) => void;
}

/**
 * Internal content component with buttons and loading state
 */
export const ConfirmContent: React.FC<ConfirmContentProps> = ({ config, onResolve }) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const [loading, setLoading] = useState(false);

  const loadingStyle = config.loadingStyle || 'button';

  const handleConfirm = async () => {
    if (config.onConfirm) {
      setLoading(true);
      try {
        await config.onConfirm();
      } finally {
        setLoading(false);
      }
    }
    onResolve({ type: ExpauseSheetResponseType.confirm });
    SheetService.close();
  };

  const handleCancel = () => {
    onResolve(null);
    SheetService.close();
  };

  // Determine if we should prevent auto-close on confirm (when we have async work)
  const hasAsyncWork = !!config.onConfirm;

  return (
    <div
      style={{ position: 'relative' }}
      {...(config.qaId ? qaAttr({ id: config.qaId }) : {})}
    >
      {/* Title */}
      <div
        style={{
          padding: responsive.spacing['2xl'],
          paddingBottom: 0,
          textAlign: 'center',
          color: theme.text.primary,
          fontSize: responsive.font.lg,
          fontWeight: responsive.fontWeight.semibold,
        }}
      >
        {config.title}
      </div>

      {/* Confirm text */}
      <div
        style={{
          padding: responsive.spacing['2xl'],
          textAlign: 'center',
          color: theme.text.primary,
          fontSize: responsive.font.base,
          lineHeight: 1.5,
          // Honour \n in l10n copy (e.g. inbox.notificationTap.retryPaymentDescription); same rule as SubscribeUserSheetContent.tsx:97.
          whiteSpace: 'pre-line',
        }}
      >
        {config.confirmText}
      </div>

      {/* CTA Buttons - responsive layout */}
      <CTAButtons
        cta={{
          buttons: [
            {
              text: config.okText || l10n.common.ok,
              onClick: handleConfirm,
              disabled: loading,
              closeOnClick: !hasAsyncWork, // Prevent auto-close if we have async work
              textColor: theme.exclusive,
              loading: loading && loadingStyle === 'button',
              qaId: 'confirm-sheet-confirm',
            },
            {
              text: config.cancelText || l10n.common.cancel,
              onClick: handleCancel,
              disabled: loading,
              qaId: 'confirm-sheet-cancel',
            },
          ],
        }}
        onClose={() => {
          // This is called by CTAButtons after onClick when closeOnClick is true
          // We handle closing manually in handleConfirm/handleCancel, so this is a no-op
        }}
      />

      {/* Loading overlay - covers title, description, and buttons */}
      {loading && loadingStyle === 'overlay' && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${theme.black}4D`,
            borderRadius: responsive.borderRadius.lg,
          }}
        >
          <ExpauseLoadingIndicator size={24} />
        </div>
      )}
    </div>
  );
};
